import { useMemo } from 'react';

import {
	CheckinOrderFragment,
	CheckoutOrderFragment,
	GdsServiceVipSubType,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
} from '@websky/graphql';

import { PassengersById, SegmentsById, State } from './types';
import { getPassengerLabel } from '../../../../utils';
import { SegmentId, ServiceId } from '../../../../types';
import { PassengerState } from '../../../../Checkout/store/passengers/reducers';
import { isCheckoutOrderGuard } from '../../../../typeguards';

export const useVariables = (
	order: CheckoutOrderFragment | CheckinOrderFragment,
	state: State,
	activeSegment?: SegmentId
) => {
	const transferServices = useMemo<GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]>(() => {
		return (
			(order?.additionalServices?.gdsServices?.services?.filter(
				service =>
					(service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType ===
					GdsServiceVipSubType.GroundTransportation
			) as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[]) ?? []
		);
	}, [order.additionalServices]);

	const notNullPriceServices = useMemo(() => {
		return transferServices.filter(transferService => transferService?.price?.amount !== 0);
	}, [transferServices]);

	const segmentsById = useMemo<SegmentsById>(() => {
		const segmentsById: SegmentsById = {};

		if (isCheckoutOrderGuard(order)) {
			order.flight.segments.forEach(segment => {
				segmentsById[segment.segment.id] = {
					city: segment.segment.departure.airport.city.name,
					date: segment.segment.departure.date
				};
			});
		}

		return segmentsById;
	}, [order]);

	const passengersById = useMemo<PassengersById>(() => {
		const passengersById: PassengersById = {};
		let number = 1;

		if (isCheckoutOrderGuard(order)) {
			order.travellers.forEach(traveller => {
				passengersById[traveller.id] = {
					number: number.toString(10),
					fullName: getPassengerLabel((traveller as unknown) as PassengerState)
				};

				number++;
			});
		}

		return passengersById;
	}, [order]);

	const selectedServicesIds = useMemo<Set<ServiceId>>(() => {
		const selectedServicesIds = new Set<ServiceId>();

		for (const [passengerId] of Object.keys(state.services)) {
			for (const [segmentId] of Object.keys(state.services[passengerId])) {
				state.services[passengerId][segmentId].forEach(service => {
					if (service?.selected) {
						selectedServicesIds.add(service.service.id);
					}
				});
			}
		}

		return selectedServicesIds;
	}, [state.services]);

	const activeSegmentServices = useMemo(() => {
		if (!activeSegment) {
			return notNullPriceServices;
		}

		return notNullPriceServices.filter(service => {
			const allowedSegments: string[] = Array.prototype.concat.apply([], service.allowedSegments);

			return allowedSegments.includes(activeSegment);
		});
	}, [notNullPriceServices, activeSegment]);

	const initialState = useMemo(() => {
		return JSON.stringify(state);
	}, []);

	const currentState = useMemo(() => {
		return JSON.stringify(state);
	}, [state]);

	const stateChanged = useMemo(() => {
		return initialState !== currentState;
	}, [initialState, currentState]);

	return {
		transferServices,
		segmentsById,
		passengersById,
		selectedServicesIds,
		activeSegmentServices,
		stateChanged
	};
};
